import React, { useState, useEffect } from 'react';
import { Download, Loader2, FileImage, Image, FileCode, Code, Blocks, AlertTriangle, FileJson } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useWorkspace } from '@/context/WorkspaceContext';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const ExportModal = ({ designId, pages, onClose, elementorCompatible = false }) => {
  const { refreshCredits } = useWorkspace();
  const [selectedPage, setSelectedPage] = useState(pages[0]?.page_name || '');
  const [selectedFormat, setSelectedFormat] = useState('png');
  const [exporting, setExporting] = useState(false);

  // Available export formats based on theme compatibility
  const EXPORT_FORMATS = [
    { id: 'png', name: 'PNG Image', icon: FileImage, description: 'High-quality image', always: true },
    { id: 'jpg', name: 'JPG Image', icon: Image, description: 'Compressed image', always: true },
    { id: 'html', name: 'HTML/CSS', icon: FileCode, description: 'Web code', always: true },
    { id: 'json', name: 'Content JSON', icon: FileJson, description: 'Download site content', always: true },
    // Elementor only shown for compatible themes
    { id: 'elementor', name: 'Elementor Template', icon: Blocks, description: 'Starting template for Elementor', elementorOnly: true },
    { id: 'figma', name: 'Figma JSON', icon: Code, description: 'Import to Figma', always: true }
  ];

  // Filter formats based on theme compatibility
  const availableFormats = EXPORT_FORMATS.filter(f => f.always || (f.elementorOnly && elementorCompatible));

  // Reset format if currently selected is no longer available
  useEffect(() => {
    if (selectedFormat === 'elementor' && !elementorCompatible) {
      setSelectedFormat('png');
    }
  }, [elementorCompatible, selectedFormat]);

  const handleExport = async () => {
    // No frontend credit check - backend handles admin bypass
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/designs/${designId}/export`,
        { page_name: selectedPage, format: selectedFormat },
        { withCredentials: true }
      );

      const data = response.data;
      
      if (selectedFormat === 'html') {
        // Download HTML file
        const htmlBlob = new Blob([data.html], { type: 'text/html' });
        downloadFile(htmlBlob, `${selectedPage}.html`);
        
        // Download CSS file
        const cssBlob = new Blob([data.css], { type: 'text/css' });
        downloadFile(cssBlob, 'styles.css');
        
        toast.success('HTML and CSS files downloaded!');
      } else if (selectedFormat === 'json') {
        // Download content JSON
        const jsonBlob = new Blob([JSON.stringify(data.content_json || data, null, 2)], { type: 'application/json' });
        downloadFile(jsonBlob, `${selectedPage}_content.json`);
        toast.success('Content JSON downloaded!');
      } else if (selectedFormat === 'elementor') {
        // Download Elementor JSON with disclosure
        const jsonBlob = new Blob([JSON.stringify(data.elementor_json, null, 2)], { type: 'application/json' });
        downloadFile(jsonBlob, `${selectedPage}_elementor_template.json`);
        
        // Also copy to clipboard
        await navigator.clipboard.writeText(JSON.stringify(data.elementor_json, null, 2));
        toast.success('Elementor template downloaded! Import this in Elementor to start editing.');
      } else if (selectedFormat === 'figma') {
        // Download Figma JSON
        const jsonBlob = new Blob([JSON.stringify(data.figma_json, null, 2)], { type: 'application/json' });
        downloadFile(jsonBlob, `${selectedPage}_figma.json`);
        
        // Also copy to clipboard
        await navigator.clipboard.writeText(JSON.stringify(data.figma_json, null, 2));
        toast.success('Figma JSON downloaded and copied to clipboard!');
      } else {
        // For PNG/JPG, capture the preview element
        toast.info('Capturing design preview...');
        
        // Find the preview element - look for the design preview container
        const previewElement = document.querySelector('[data-testid="design-visual-preview"]') || 
                               document.querySelector('.design-preview-container') ||
                               document.querySelector('[class*="preview"]');
        
        if (previewElement) {
          try {
            const canvas = await html2canvas(previewElement, {
              scale: 2, // Higher quality
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false
            });
            
            // Convert to blob and download
            canvas.toBlob((blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${selectedPage}.${selectedFormat}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success(`${selectedFormat.toUpperCase()} downloaded successfully!`);
              } else {
                toast.error('Failed to generate image');
              }
            }, selectedFormat === 'png' ? 'image/png' : 'image/jpeg', 0.9);
          } catch (captureErr) {
            console.error('Capture error:', captureErr);
            toast.error('Failed to capture preview. Try using browser screenshot instead.');
          }
        } else {
          toast.error('Preview element not found. Please ensure design preview is visible.');
        }
      }
      
      await refreshCredits();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Download className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-zinc-900">Export Design</h3>
            <p className="text-sm text-zinc-500">Download your design in various formats</p>
          </div>
        </div>

        {/* Page Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-zinc-700 mb-2">Select Page</label>
          <select
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {pages.map((page) => (
              <option key={page.page_name} value={page.page_name}>
                {page.page_name}
              </option>
            ))}
          </select>
        </div>

        {/* Format Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-zinc-700 mb-2">Export Format</label>
          <div className="grid grid-cols-2 gap-3">
            {availableFormats.map((format) => {
              const Icon = format.icon;
              const isElementor = format.id === 'elementor';
              return (
                <button
                  key={format.id}
                  onClick={() => setSelectedFormat(format.id)}
                  className={`p-3 rounded-lg border-2 text-left transition ${
                    selectedFormat === format.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${selectedFormat === format.id ? 'text-indigo-600' : 'text-zinc-500'}`} />
                    <span className="font-medium text-sm">{format.name}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{format.description}</p>
                  {isElementor && (
                    <p className="text-xs text-amber-600 mt-1">Starting template only</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Elementor Disclosure */}
        {selectedFormat === 'elementor' && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Starting Template Only</p>
                <p className="text-xs text-amber-700 mt-1">
                  This exports a starting Elementor template for this theme. Your content structure 
                  will be included, but you&apos;ll need to style and customize it in Elementor. 
                  This is <strong>not</strong> a 1:1 conversion of your finished design.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={exporting}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
